import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import residencesService from '../../../services/carehome_manager/residencesService';
import { TabSkeleton } from '../../../skeletons';

/**
 * Continence — the bladder and bowel picture, built entirely from the
 * toileting actions carers log in care notes. Nothing is entered here; this is
 * the read side of `Urine output`, `Bowel movement`, `Continence pad care`,
 * `Toileting support` and `Catheter care`.
 *
 * The two figures that matter clinically lead: the day's urine total, and how
 * long it has been since a bowel movement. The second is the constipation
 * signal a home gets inspected on, so it is called out rather than buried in
 * the table.
 */

const KIND_STYLES = {
    urine: { label: 'Urine', className: 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-900/25 dark:border-amber-800' },
    bowel: { label: 'Bowel', className: 'text-orange-700 bg-orange-50 border-orange-200 dark:text-orange-300 dark:bg-orange-900/25 dark:border-orange-800' },
    pad: { label: 'Pad', className: 'text-blue-700 bg-blue-50 border-blue-200 dark:text-blue-300 dark:bg-blue-900/25 dark:border-blue-800' },
    toileting: { label: 'Toileting', className: 'text-slate-700 bg-slate-50 border-slate-200 dark:text-slate-300 dark:bg-slate-800/50 dark:border-slate-700' },
    catheter: { label: 'Catheter', className: 'text-purple-700 bg-purple-50 border-purple-200 dark:text-purple-300 dark:bg-purple-900/25 dark:border-purple-800' },
    other: { label: 'Other', className: 'text-gray-600 bg-gray-50 border-gray-200 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-700' },
};

/** Bars sized against the biggest day, so a flat week still reads as flat. */
const MiniChart = ({ data, unit, colour }) => {
    const max = Math.max(...data.map((d) => d.value), 1);
    return (
        <div className="flex items-end gap-2 h-[120px]">
            {data.map((d) => {
                const pct = (d.value / max) * 100;
                const day = new Date(`${d.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short' });
                return (
                    <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full gap-1">
                        <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                            {d.value > 0 ? d.value : ''}
                        </span>
                        <div
                            className={`w-full rounded-t-md ${colour} ${d.value === 0 ? 'opacity-25' : ''}`}
                            style={{ height: `${Math.max(pct, d.value > 0 ? 6 : 3)}%` }}
                            title={`${d.date}: ${d.value}${unit}`}
                        />
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">{day}</span>
                    </div>
                );
            })}
        </div>
    );
};

const ContinenceTab = () => {
    const { id: residenceId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [kindFilter, setKindFilter] = useState('all');

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                setLoading(true);
                const res = await residencesService.getContinenceData(residenceId);
                if (!cancelled) {
                    setData(res.data);
                    setError(null);
                }
            } catch (e) {
                if (!cancelled) setError('Could not load continence data.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        if (residenceId) load();
        return () => { cancelled = true; };
    }, [residenceId]);

    if (loading) return <TabSkeleton />;
    if (error) {
        return <div className="p-8 text-center text-[13px] text-red-500">{error}</div>;
    }

    const summary = data?.summary || {};
    const records = data?.records || [];
    const days = data?.days || 7;

    const visible = kindFilter === 'all' ? records : records.filter((r) => r.kind === kindFilter);
    const kindsPresent = [...new Set(records.map((r) => r.kind))];

    // "Not recorded" is deliberately distinct from "0 days ago" — a resident
    // with nothing logged is not a resident who went today.
    const bowelValue = summary.lastBowelAt
        ? (summary.daysSinceLastBowel === 0
            ? 'Today'
            : `${summary.daysSinceLastBowel} day${summary.daysSinceLastBowel === 1 ? '' : 's'} ago`)
        : 'Not recorded';

    const tiles = [
        {
            label: 'Urine today',
            value: `${summary.urineTodayMl || 0} ml`,
            hint: `${summary.urineTodayCount || 0} recorded`,
        },
        {
            label: `Urine (last ${days} days)`,
            value: `${summary.urineWindowMl || 0} ml`,
            // Averaged over the days that have a reading, not the whole window.
            hint: summary.urineDaysRecorded
                ? `${summary.urineDailyAverageMl} ml/day over ${summary.urineDaysRecorded} day${summary.urineDaysRecorded === 1 ? '' : 's'}`
                : 'No readings',
        },
        {
            label: 'Last bowel movement',
            value: bowelValue,
            hint: `${summary.bowelWindowCount || 0} in ${days} days`,
            alert: summary.bowelConcern,
        },
        {
            label: 'Pad changes today',
            value: `${summary.padTodayCount || 0}`,
            hint: `${summary.padWindowCount || 0} in ${days} days`,
        },
    ];

    return (
        <div className="animate-in fade-in duration-300">
            <h2 className="text-[20px] font-bold text-gray-800 dark:text-gray-100 mb-1">
                Continence Record
            </h2>
            <p className="text-[12px] text-gray-500 dark:text-gray-400 mb-6">
                Built from the toileting actions logged in care notes. Recorded on the mobile app.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                {tiles.map((tile) => (
                    <div
                        key={tile.label}
                        className={`bg-white dark:bg-[#262b33] border rounded-[16px] shadow-sm px-5 py-4 ${
                            tile.alert
                                ? 'border-red-200 dark:border-red-800'
                                : 'border-gray-50 dark:border-gray-800'
                        }`}
                    >
                        <div className="text-[11px] font-bold text-[#6B7280] dark:text-gray-400 uppercase tracking-wider">
                            {tile.label}
                        </div>
                        <div className={`mt-1 text-[18px] font-bold ${
                            tile.alert ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-100'
                        }`}>
                            {tile.value}
                        </div>
                        {tile.hint && (
                            <div className="mt-0.5 text-[11px] font-medium text-gray-400 dark:text-gray-500">
                                {tile.hint}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {summary.bowelConcern && (
                <div className="flex items-start gap-3 rounded-[16px] border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-5 py-4 mb-8">
                    <span className="text-[16px] leading-none mt-0.5">⚠</span>
                    <div>
                        <div className="text-[13px] font-semibold text-red-700 dark:text-red-300">
                            {summary.lastBowelAt
                                ? `No bowel movement recorded for ${summary.daysSinceLastBowel} days`
                                : 'No bowel movement has ever been recorded'}
                        </div>
                        <div className="text-[12px] text-red-600/80 dark:text-red-400/80 mt-0.5">
                            {summary.bowelConcernDays} days or more is the usual point to review under the
                            constipation protocol. Check whether it went unrecorded before escalating.
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="bg-white dark:bg-[#262b33] border border-gray-50 dark:border-gray-800 rounded-[24px] shadow-sm px-8 py-6">
                    <h3 className="text-[16px] font-semibold text-gray-800 dark:text-gray-100 mb-4">
                        Urine output <span className="text-[12px] font-normal text-gray-400">ml per day</span>
                    </h3>
                    <MiniChart data={data.urineChart || []} unit="ml" colour="bg-amber-400" />
                </div>

                <div className="bg-white dark:bg-[#262b33] border border-gray-50 dark:border-gray-800 rounded-[24px] shadow-sm px-8 py-6">
                    <h3 className="text-[16px] font-semibold text-gray-800 dark:text-gray-100 mb-4">
                        Bowel movements <span className="text-[12px] font-normal text-gray-400">per day</span>
                    </h3>
                    <MiniChart data={data.bowelChart || []} unit="" colour="bg-orange-400" />
                </div>
            </div>

            {/* Bristol spread — which stool types this resident actually passes.
                A run of 1s and 2s is constipation; 6s and 7s is the opposite. */}
            {(data.bristolSpread || []).some((b) => b.count > 0) && (
                <div className="bg-white dark:bg-[#262b33] border border-gray-50 dark:border-gray-800 rounded-[24px] shadow-sm px-8 py-6 mb-8">
                    <h3 className="text-[16px] font-semibold text-gray-800 dark:text-gray-100 mb-4">
                        Bristol stool types recorded
                    </h3>
                    <div className="flex gap-3">
                        {data.bristolSpread.map((b) => {
                            const max = Math.max(...data.bristolSpread.map((x) => x.count), 1);
                            return (
                                <div key={b.type} className="flex-1 text-center">
                                    <div className="h-[70px] flex items-end justify-center">
                                        <div
                                            className={`w-full rounded-t-md ${b.count ? 'bg-orange-400' : 'bg-gray-200 dark:bg-gray-700'}`}
                                            style={{ height: `${b.count ? Math.max((b.count / max) * 100, 10) : 4}%` }}
                                        />
                                    </div>
                                    <div className="text-[12px] font-bold text-gray-700 dark:text-gray-200 mt-1">{b.count}</div>
                                    <div className="text-[11px] text-gray-400">Type {b.type}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="bg-white dark:bg-[#262b33] border border-gray-50 dark:border-gray-800 rounded-[24px] shadow-sm overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-3 px-8 py-5">
                    <h3 className="text-[16px] font-semibold text-gray-800 dark:text-gray-100">History</h3>
                    <div className="inline-flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                        {['all', ...kindsPresent].map((k) => (
                            <button
                                key={k}
                                onClick={() => setKindFilter(k)}
                                className={`px-3 py-1 rounded-md text-[12px] font-medium capitalize transition ${
                                    kindFilter === k
                                        ? 'bg-white dark:bg-[#262b33] text-blue-600 shadow-sm'
                                        : 'text-gray-500'
                                }`}
                            >
                                {k === 'all' ? 'All' : (KIND_STYLES[k]?.label || k)}
                            </button>
                        ))}
                    </div>
                </div>

                {visible.length === 0 ? (
                    <div className="px-8 py-12 text-center text-[13px] text-gray-400">
                        Nothing recorded yet. Continence entries appear here as carers log them.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[720px]">
                            <thead>
                                <tr className="text-[11px] font-bold text-[#6B7280] dark:text-gray-400 uppercase tracking-wider border-t border-gray-100 dark:border-gray-800">
                                    <th className="text-left px-8 py-3">When</th>
                                    <th className="text-left px-4 py-3">Type</th>
                                    <th className="text-left px-4 py-3">Recorded</th>
                                    <th className="text-left px-4 py-3">Detail</th>
                                    <th className="text-left px-8 py-3">Staff</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map((r) => {
                                    const style = KIND_STYLES[r.kind] || KIND_STYLES.other;
                                    return (
                                        <tr
                                            key={r.id}
                                            className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-[#2b313a]"
                                        >
                                            <td className="px-8 py-3 whitespace-nowrap">
                                                <div className="text-[13px] font-medium text-gray-800 dark:text-gray-100">{r.time}</div>
                                                <div className="text-[11px] text-gray-400">{r.date}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${style.className}`}>
                                                    {style.label}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-[13px] font-medium text-gray-800 dark:text-gray-100">{r.label}</div>
                                                {r.summary && (
                                                    <div className="text-[11px] text-blue-600 dark:text-blue-400">{r.summary}</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-wrap gap-x-3 gap-y-0.5 max-w-[380px]">
                                                    {(r.details || []).map((d, i) => (
                                                        <span key={i} className="text-[11px] text-gray-500 dark:text-gray-400">
                                                            {d.label}:{' '}
                                                            <span className="font-medium text-gray-700 dark:text-gray-200">{d.value}</span>
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="px-8 py-3 text-[12px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                                {r.staff}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContinenceTab;
